import React, { useContext } from 'react'

import SavedItemRemoveButton from '../ItemCard/Common/SavedItemRemoveButton'
import FlightItemCardHeading from '../ItemCard/Flight/FlightItemCardHeading'
import { ItemCardVariant } from '../ItemCard/ItemCard'

import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import {
  fareTypeLabelMap,
  fareCabinLabelMap,
  FlightsFareTypes,
} from 'constants/flight'
import GeoContext from 'contexts/geoContext'
import noop from 'lib/function/noop'
import {
  formatOccupantsShort,
  sumUpOccupancies,
} from 'lib/offer/occupancyUtils'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useLowestFlightPrice } from 'tripPlanner/hooks/api'
import { LEFlightItem } from 'tripPlanner/types/tripItem'
import { formatShortDateRange } from 'tripPlanner/utils'

export interface Props {
  item: LEFlightItem
  onClickTripName?: () => void
  variant: ItemCardVariant
  searchForFlight: () => void
}

export default function FlightSearchTileContent({
  item,
  onClickTripName = noop,
  variant,
  searchForFlight,
}: Props) {
  const {
    originAirportCode,
    destinationAirportCode,
    savedItemData: {
      currencyCode,
      regionCode,
      adultPrice,
      fareClass,
      fareType,
      totalPrice,
      travellerCounts,
    },
  } = item

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const viewAsTemplate = tripContext?.viewAsTemplate ?? false
  const effectiveCurrencyCode = viewAsTemplate ? currentCurrency : currencyCode
  const { data: price, isLoading: priceLoading } = useLowestFlightPrice({
    originAirportCode,
    destinationAirportCode,
    currencyCode: effectiveCurrencyCode,
    regionCode: viewAsTemplate ? currentRegionCode : regionCode,
    shouldQuery:
      (viewAsTemplate || !adultPrice) && fareType === FlightsFareTypes.RETURN,
  })

  // when viewed in a curated trip, ignore saved flight price
  const minAdultPrice = viewAsTemplate ? price : adultPrice ?? price
  const showPrice = !priceLoading && !!minAdultPrice

  const travellerCount: App.Occupants = travellerCounts ?? {
    adults: 0,
  }
  const showTotal = totalPrice && sumUpOccupancies([travellerCount]) > 1
  const fareTypeLable = fareTypeLabelMap[fareType]
  const fareClassLabel = fareCabinLabelMap[fareClass]

  const description = [
    formatShortDateRange(item),
    fareClassLabel,
    fareTypeLable,
    formatOccupantsShort(travellerCount),
  ]
    .filter(Boolean)
    .join(' • ')
  return (
    <>
      <Group
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="space-between"
      >
        <BodyTextBlock variant="small" startIcon={<LinePlaneFlyIcon />}>
          Flight
        </BodyTextBlock>
        {canEdit && <SavedItemRemoveButton item={item} />}
      </Group>
      <VerticalSpacer gap={4}>
        <FlightItemCardHeading item={item} />
        <BodyText variant="medium">{description}</BodyText>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        {variant === 'drawer' && tripContext && (
          <StructureLabel variant="information">
            <BodyTextBlock variant="small">Added to </BodyTextBlock>
            <TextLink
              size="caption"
              onClick={(e) => {
                e.stopPropagation()
                onClickTripName()
              }}
            >
              {tripContext.name}
            </TextLink>
          </StructureLabel>
        )}
        <Group
          direction="horizontal"
          horizontalAlign={showPrice ? 'space-between' : 'end'}
          verticalAlign="end"
        >
          {showPrice && (
            <div>
              <Caption variant="medium">{fareTypeLable} from</Caption>
              <div>
                <Heading as="span" variant="heading6">
                  <FormatCurrency
                    value={minAdultPrice}
                    currency={effectiveCurrencyCode}
                    format="dynamicDollar"
                  />
                  *
                </Heading>
                <BodyText as="span" variant="small">
                  {' '}
                  /adult
                </BodyText>
                {showTotal && (
                  <Caption variant="medium" colour="neutral-three">
                    Total from <FormatCurrency value={totalPrice} />
                  </Caption>
                )}
              </div>
            </div>
          )}
          {canEdit && (
            <TextButton
              kind={variant === 'drawer' ? 'primary' : 'secondary'}
              onClick={searchForFlight}
            >
              Search flights
            </TextButton>
          )}
          {!canEdit && (
            <TextButton kind="secondary" onClick={searchForFlight}>
              Find flights
            </TextButton>
          )}
        </Group>
        {showPrice && (
          <Caption colour="neutral-four" variant="medium">
            *Price and availability are subject to change.
          </Caption>
        )}
      </VerticalSpacer>
    </>
  )
}
